import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, X, Truck, Thermometer, Clock, CheckCircle2, MapPin, Pencil, PackageCheck, PlayCircle, DollarSign, Wallet, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  programado: { label: "Programado", color: "bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300", icon: Clock },
  en_ruta: { label: "En ruta", color: "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300", icon: PlayCircle },
  entregado: { label: "Entregado", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300", icon: X },
};

const empty = {
  shipment_date: new Date().toISOString().slice(0, 10),
  driver_name: "", vehicle_plate: "", customer_id: "", customer_name: "",
  sale_folios: [], temperature_out_c: "", temperature_in_c: "", time_out: "", time_in: "",
  origin: "Planta", destination: "", status: "programado", notes: "",
  freight_supplier_id: "", freight_supplier_name: "", freight_cost: "",
  freight_payment_method: "contado", freight_credit_days: 0, freight_invoice_number: "", freight_notes: "",
};

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Embarques() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [freightSuppliers, setFreightSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [it, s, c, sa, sup] = await Promise.all([
      api.get("/shipments"),
      api.get("/shipments/summary"),
      api.get("/customers"),
      api.get("/sales"),
      api.get("/suppliers"),
    ]);
    setItems(it.data);
    setSummary(s.data);
    setCustomers(c.data);
    setSales(sa.data);
    // Filtrar proveedores con tag "fletes" (o mostrar todos si ninguno tiene tag)
    const withTag = (sup.data || []).filter((x) => (x.types || []).includes("fletes") && x.active !== false);
    setFreightSuppliers(withTag.length ? withTag : (sup.data || []).filter((x) => x.active !== false));
  };
  useEffect(() => { load(); }, []);

  // Auto-abrir formulario con datos de la venta si viene ?sale=VTA-...
  useEffect(() => {
    const sf = params.get("sale");
    const cn = params.get("customer");
    const cid = params.get("customer_id");
    if (sf && !open) {
      setEditing(null);
      setForm({ ...empty, customer_id: cid || "", customer_name: cn || "", sale_folios: [sf] });
      setOpen(true);
      // Limpiar query params
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...empty,
      ...s,
      sale_folios: s.sale_folios || [],
      temperature_out_c: s.temperature_out_c ?? "",
      temperature_in_c: s.temperature_in_c ?? "",
      time_out: s.time_out ?? "",
      time_in: s.time_in ?? "",
      notes: s.notes ?? "",
      destination: s.destination ?? "",
      customer_id: s.customer_id ?? "",
      freight_supplier_id: s.freight_supplier_id ?? "",
      freight_supplier_name: s.freight_supplier_name ?? "",
      freight_cost: s.freight_cost ?? "",
      freight_payment_method: s.freight_payment_method ?? "contado",
      freight_credit_days: s.freight_credit_days ?? 0,
      freight_invoice_number: s.freight_invoice_number ?? "",
      freight_notes: s.freight_notes ?? "",
    });
    setOpen(true);
  };

  const pickFreightSupplier = (id) => {
    if (id === "_none") return setForm((f) => ({ ...f, freight_supplier_id: "", freight_supplier_name: "" }));
    const s = freightSuppliers.find((x) => x.id === id);
    if (!s) return;
    setForm((f) => ({
      ...f,
      freight_supplier_id: s.id,
      freight_supplier_name: s.name,
      freight_payment_method: (s.default_credit_days || 0) > 0 ? "credito" : (f.freight_payment_method || "contado"),
      freight_credit_days: s.default_credit_days || f.freight_credit_days || 0,
    }));
  };

  const pickCust = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, destination: c.address || f.destination }));
  };

  const toggleSaleFolio = (folio) => setForm((f) => ({ ...f, sale_folios: f.sale_folios.includes(folio) ? f.sale_folios.filter((x) => x !== folio) : [...f.sale_folios, folio] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_name || !form.vehicle_plate || !form.customer_name) return toast.error("Chofer, placa y cliente son requeridos");
    try {
      const payload = {
        ...form,
        temperature_out_c: form.temperature_out_c ? parseFloat(form.temperature_out_c) : null,
        temperature_in_c: form.temperature_in_c ? parseFloat(form.temperature_in_c) : null,
        customer_id: form.customer_id || null,
        freight_supplier_id: form.freight_supplier_id || null,
        freight_cost: form.freight_cost ? parseFloat(form.freight_cost) : 0,
        freight_credit_days: form.freight_payment_method === "credito" ? parseInt(form.freight_credit_days) || 0 : 0,
      };
      if (editing) await api.put(`/shipments/${editing.id}`, payload);
      else await api.post("/shipments", payload);
      toast.success(editing ? "Embarque actualizado" : "Embarque creado");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const setStatus = async (s, newStatus) => {
    try {
      const payload = { ...s, status: newStatus };
      if (newStatus === "en_ruta" && !s.time_out) payload.time_out = new Date().toTimeString().slice(0, 5);
      if (newStatus === "entregado" && !s.time_in) payload.time_in = new Date().toTimeString().slice(0, 5);
      await api.put(`/shipments/${s.id}`, payload);
      toast.success(`Estado: ${STATUS[newStatus].label}`);
      load();
    } catch { toast.error("Error"); }
  };

  const availableSales = sales.filter((sale) => sale.customer_id === form.customer_id || sale.customer_name === form.customer_name);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="embarques-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Embarques</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Salidas de producto: chofer, placa, temperatura de salida y entrega, y ventas ligadas.</p>
        </div>
        {!open && <Button onClick={openNew} data-testid="new-shipment-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nuevo embarque</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <KPI icon={Truck} label="Hoy" value={summary.today_count || 0} tone="blue" />
        <KPI icon={PlayCircle} label="En ruta" value={summary.en_ruta_count || 0} tone="blue" />
        <KPI icon={Clock} label="Programados" value={summary.programados || 0} tone="stone" />
        <KPI icon={PackageCheck} label="Entregados 7 días" value={summary.delivered_week || 0} tone="emerald" />
        <KPI icon={Thermometer} label="Temp. promedio entrega" value={summary.avg_temp_delivery != null ? `${summary.avg_temp_delivery}°C` : "—"} tone="amber" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="freight-kpis">
        <KPI icon={DollarSign} label="Costo total flete (mes)" value={money(summary.total_freight_month)} tone="amber" />
        <KPI icon={Truck} label="Flete promedio / embarque" value={money(summary.avg_freight_per_shipment)} tone="stone" />
        <KPI icon={PackageCheck} label="Embarques con flete (mes)" value={summary.freight_ships_month_count || 0} tone="blue" />
      </div>

      {open && (
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg">{editing ? `Editar ${editing.folio}` : "Nuevo embarque"}</h2>
            <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Fecha *"><Input type="date" value={form.shipment_date} onChange={(e) => setForm({ ...form, shipment_date: e.target.value })} data-testid="ship-date" /></Field>
              <Field label="Chofer *"><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="ship-driver" placeholder="Nombre del chofer" /></Field>
              <Field label="Placa vehículo *"><Input value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} data-testid="ship-plate" /></Field>
              <Field label="Estado">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="ship-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cliente *</Label>
                <Select value={form.customer_id || "_none"} onValueChange={(v) => v === "_none" ? setForm({ ...form, customer_id: "", customer_name: "" }) : pickCust(v)}>
                  <SelectTrigger className="mt-1" data-testid="ship-customer"><SelectValue placeholder="Elegir cliente…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sin cliente registrado —</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })} className="mt-1" placeholder="… o captura nombre libre" />
              </div>
              <Field label="Origen"><Input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} /></Field>
              <Field label="Destino"><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Dirección o ciudad" /></Field>
              <Field label="Hora salida"><Input type="time" value={form.time_out} onChange={(e) => setForm({ ...form, time_out: e.target.value })} data-testid="ship-time-out" /></Field>
              <Field label="Temp. salida °C"><Input type="number" step="0.1" value={form.temperature_out_c} onChange={(e) => setForm({ ...form, temperature_out_c: e.target.value })} placeholder="4-6°C ideal" data-testid="ship-temp-out" /></Field>
              <Field label="Hora llegada"><Input type="time" value={form.time_in} onChange={(e) => setForm({ ...form, time_in: e.target.value })} /></Field>
              <Field label="Temp. llegada °C"><Input type="number" step="0.1" value={form.temperature_in_c} onChange={(e) => setForm({ ...form, temperature_in_c: e.target.value })} data-testid="ship-temp-in" /></Field>
            </div>

            {availableSales.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400 mb-1.5 block">Ventas del cliente para vincular ({form.sale_folios.length} seleccionadas)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {availableSales.map((sa) => {
                    const on = form.sale_folios.includes(sa.folio);
                    return (
                      <button type="button" key={sa.id} onClick={() => toggleSaleFolio(sa.folio)} className={`text-xs px-3 py-1.5 rounded-md font-outfit font-medium transition-colors ${on ? "bg-[#1E3A8A] text-white" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                        {sa.folio} · {sa.sale_date} · ${sa.total?.toFixed(2)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" />
            </div>
            <div className="border-t border-stone-200 dark:border-stone-800 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-amber-600" />
                <h3 className="font-outfit font-semibold text-sm uppercase tracking-wider text-stone-700 dark:text-stone-300">Flete contratado (opcional)</h3>
                <span className="text-[10px] text-stone-500">Al marcar entregado se genera Compra en CxP</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Proveedor de flete</Label>
                  <Select value={form.freight_supplier_id || "_none"} onValueChange={pickFreightSupplier}>
                    <SelectTrigger className="mt-1" data-testid="freight-supplier"><SelectValue placeholder="Elegir proveedor…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Sin proveedor / captura libre —</SelectItem>
                      {freightSuppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{(s.types || []).includes("fletes") ? " · flete" : ""}{s.default_credit_days ? ` · ${s.default_credit_days}d` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={form.freight_supplier_name}
                    onChange={(e) => setForm({ ...form, freight_supplier_name: e.target.value, freight_supplier_id: "" })}
                    className="mt-1"
                    placeholder="… o captura nombre libre del transportista"
                    data-testid="freight-supplier-name"
                  />
                </div>
                <Field label="Costo del flete">
                  <Input type="number" step="0.01" min="0" value={form.freight_cost} onChange={(e) => setForm({ ...form, freight_cost: e.target.value })} placeholder="$0.00" data-testid="freight-cost" />
                </Field>
                <Field label="Factura #">
                  <Input value={form.freight_invoice_number} onChange={(e) => setForm({ ...form, freight_invoice_number: e.target.value })} placeholder="opcional" />
                </Field>
                <Field label="Forma de pago">
                  <Select value={form.freight_payment_method} onValueChange={(v) => setForm({ ...form, freight_payment_method: v })}>
                    <SelectTrigger data-testid="freight-pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contado"><span className="flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Contado</span></SelectItem>
                      <SelectItem value="credito"><span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Crédito</span></SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Días crédito">
                  <Input type="number" min="0" value={form.freight_credit_days} disabled={form.freight_payment_method === "contado"} onChange={(e) => setForm({ ...form, freight_credit_days: e.target.value })} data-testid="freight-credit-days" />
                </Field>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas flete</Label>
                  <Input value={form.freight_notes} onChange={(e) => setForm({ ...form, freight_notes: e.target.value })} className="mt-1" placeholder="Ruta, kilometraje, condiciones…" />
                </div>
              </div>
              {editing?.freight_purchase_id && (
                <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Compra ligada en Cuentas por Pagar ya generada al entregar este embarque.
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="ship-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{editing ? "Guardar" : "Crear embarque"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <Truck className="w-4 h-4 text-stone-500" />
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Historial</h3>
          <span className="text-xs text-stone-500 ml-auto">{items.length} embarques</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Folio", "Fecha", "Estado", "Cliente", "Chofer / Placa", "Ruta", "Temp.", "Flete", "Ventas", "Acciones"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-stone-500">Sin embarques registrados.</td></tr>
              ) : items.map((s) => {
                const st = STATUS[s.status] || STATUS.programado;
                const Icon = st.icon;
                return (
                  <tr key={s.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`ship-row-${s.folio}`}>
                    <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">
                      <Link to={`/trazabilidad?lote=${s.folio}`} className="hover:underline" data-testid={`ship-trace-link-${s.folio}`}>{s.folio}</Link>
                    </td>
                    <td className="px-3 py-3 text-xs tabular-nums text-stone-600 dark:text-stone-400">{s.shipment_date}</td>
                    <td className="px-3 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${st.color}`}><Icon className="w-3 h-3" /> {st.label}</span></td>
                    <td className="px-3 py-3 text-sm text-stone-800 dark:text-stone-200">{s.customer_name}</td>
                    <td className="px-3 py-3 text-xs">
                      <div className="text-stone-800 dark:text-stone-200">{s.driver_name}</div>
                      <div className="text-stone-500 font-mono">{s.vehicle_plate}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-stone-600 dark:text-stone-400 max-w-[180px] truncate"><MapPin className="w-3 h-3 inline mr-1" /> {s.origin || "?"} → {s.destination || "?"}</td>
                    <td className="px-3 py-3 text-xs tabular-nums">
                      <div><span className="text-stone-500">out:</span> {s.temperature_out_c != null ? `${s.temperature_out_c}°` : "—"}</div>
                      <div><span className="text-stone-500">in: </span>{s.temperature_in_c != null ? `${s.temperature_in_c}°` : "—"}</div>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {(s.freight_cost || 0) > 0 ? (
                        <div>
                          <div className="tabular-nums font-medium text-amber-700 dark:text-amber-400">{money(s.freight_cost)}</div>
                          <div className="text-stone-500 truncate max-w-[140px]">{s.freight_supplier_name || "—"}</div>
                          {s.freight_purchase_id && <div className="text-[9px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">→ CxP</div>}
                        </div>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs font-mono text-[#1E3A8A] dark:text-blue-400">
                      {(s.sale_folios || []).length > 0 ? s.sale_folios.slice(0, 2).map((f) => <div key={f}>{f}</div>) : "—"}
                      {(s.sale_folios || []).length > 2 && <div className="text-stone-500">+{s.sale_folios.length - 2} más</div>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {s.status === "programado" && <Button size="sm" onClick={() => setStatus(s, "en_ruta")} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">Salir</Button>}
                        {s.status === "en_ruta" && <Button size="sm" onClick={() => setStatus(s, "entregado")} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Entregado</Button>}
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setToDelete(s)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar embarque {toDelete?.folio}?</AlertDialogTitle><AlertDialogDescription>Las ventas vinculadas quedarán sin folio de embarque.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/shipments/${toDelete.id}`); toast.success("Eliminado"); setToDelete(null); load(); } catch { toast.error("Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  stone: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const KPI = ({ icon: Icon, label, value, tone = "blue" }) => (
  <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
      </div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${TONES[tone]}`}><Icon className="w-4 h-4" strokeWidth={1.75} /></div>
    </div>
  </Card>
);
